import { cloneElement, FC, forwardRef, ReactElement, ReactNode } from 'react';
import {
  Breadcrumbs as MuiBreadcrumbs,
  BreadcrumbsProps as MuiBreadcrumbsProps,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { ChevronLeft, ChevronRight, FolderOpen, Home } from '@o2/icons';
import Stack from '../Stack';
import { styled } from '@mui/material/styles';
import BreadcrumbsItem, { BreadcrumbsItemProps } from './BreadcrumbsItem';

export interface BreadcrumbsProps extends Omit<MuiBreadcrumbsProps, 'children'> {
  /**
   * Array of Breadcrumbs items, prop replaces children prop, which is omitted from original props.
   */
  items: ReactElement<BreadcrumbsItemProps, Breadcrumbs>[];
  startIcon?: ReactNode;
  othersIcon?: ReactNode;
}

const StyledChevronLeft = styled(ChevronLeft, {
  name: 'ChevronLeft',
  slot: 'root',
})({
  fontSize: '14px',
  cursor: 'pointer',
});

const StyledHome = styled(Home, {
  name: 'Home',
  slot: 'root',
})({
  fontSize: '14px',
  color: 'inherit',
});

const BreadcrumbsMobileRoot = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  alignItems: 'center',
  ' * .MuiSvgIcon-root': {
    display: 'none',
  },
}));

type Breadcrumbs = FC<BreadcrumbsProps> & {
  Item: typeof BreadcrumbsItem;
};

//@ts-expect-error: Property 'Item' is missing in type (FIXME)
const Breadcrumbs: Breadcrumbs = forwardRef<HTMLDivElement, BreadcrumbsProps>(
  (
    {
      items,
      startIcon = <Home sx={{ fontSize: '14px' }} />,
      othersIcon = <FolderOpen sx={{ fontSize: '14px' }} />,
      separator = <ChevronRight sx={{ fontSize: '14px' }} />,
      ...props
    },
    ref
  ) => {
    const useSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const mobileActiveIndex = items.length > 1 ? items.length - 2 : 0;

    if (
      items?.[mobileActiveIndex]?.type !== undefined &&
      items?.[mobileActiveIndex]?.type !== (BreadcrumbsItem as unknown)
    )
      throw new Error('Only Breadcrumbs.Item components allowed in items prop');

    return useSmall ? (
      <BreadcrumbsMobileRoot direction="row">
        {mobileActiveIndex === 0 ? (
          <StyledHome />
        ) : (
          <StyledChevronLeft color="primary" onClick={(e) => items?.[mobileActiveIndex]?.props?.onClick?.(e)} />
        )}
        {items[mobileActiveIndex]}
      </BreadcrumbsMobileRoot>
    ) : (
      <MuiBreadcrumbs separator={separator} ref={ref} {...props}>
        {startIcon || othersIcon
          ? items.map((i, k) => {
              if (!i.props?.icon) {
                if (k === 0) {
                  if (startIcon) {
                    return cloneElement(i, { icon: startIcon });
                  } else return i;
                }

                if (othersIcon) {
                  return cloneElement(i, { icon: othersIcon });
                } else return i;
              } else return i;
            })
          : items}
      </MuiBreadcrumbs>
    );
  }
);

Breadcrumbs.Item = BreadcrumbsItem;

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
